import { AUTH_REGION } from './constants';

/**
 * MapLibre transformRequest signature for ALS V2: given a URL it returns the
 * (possibly SigV4-signed) URL. The helper signs into the query string and never
 * returns headers, so the return is just `{ url }`.
 */
export type MapTransformRequest = (
  url: string,
  resourceType?: string
) => { url: string };

/**
 * Builds a transformRequest authenticated as the current Cognito user.
 *
 * It signs with the identity pool credentials Amplify already caches and
 * refreshes, rather than fetching a second set for every map.
 *
 * The auth helper (and its `@aws-sdk/credential-providers` dependency) and
 * `aws-amplify/auth` are imported dynamically here so the AWS SDK slice is
 * split into its own chunk and only downloads when a map actually mounts —
 * not whenever a module that imports BambiMapView is evaluated.
 */
export async function createMapTransformRequest(): Promise<MapTransformRequest> {
  const [{ withCredentialProvider }, { fetchAuthSession }] = await Promise.all([
    import('@aws/amazon-location-utilities-auth-helper'),
    import('aws-amplify/auth'),
  ]);

  const authHelper = await withCredentialProvider(async () => {
    const { credentials } = await fetchAuthSession();
    if (!credentials) throw new Error('No AWS credentials for this session');
    return credentials;
  }, String(AUTH_REGION));

  return authHelper.getMapAuthenticationOptions().transformRequest;
}
